import { z } from 'zod'
import type { ApiClient } from '../apiClient'

const kpiMetadataItemSchema = z.object({
  kpi_id: z.string(),
  kpi_root: z.string(),
  driver: z.string().nullable(),
  value_type: z.string().nullable(),
  display_root: z.string().nullable(),
  display_driver: z.string().nullable(),
  display_value_type: z.string().nullable(),
})

const kpiMetadataSchema = z.object({
  items: z.array(kpiMetadataItemSchema),
  control_label: z.string(),
})

const campaignTimeslotItemSchema = z.object({
  value: z.string(),
  label: z.string(),
  from: z.string().optional(),
  to: z.string().optional(),
  // Campaigns C1 (mig 0084): attribution keys — optional+nullable so an un-migrated
  // backend (or a Frame row's NULLs) still validates; the FE derives the predicate
  // from the non-null subset (campaignPredicate.ts).
  topic_l3: z.string().nullable().optional(),
  topic_l4: z.string().nullable().optional(),
  brand_casting_layer_l1: z.string().nullable().optional(),
  campaign_type: z.string().nullable().optional(),
})
export type CampaignTimeslotItem = z.infer<typeof campaignTimeslotItemSchema>

const campaignTimeslotsSchema = z.object({
  items: z.array(campaignTimeslotItemSchema),
  control_label: z.string(),
})

const timeframeFramesSchema = z.object({
  items: z.array(campaignTimeslotItemSchema),
  control_label: z.string(),
})

const timeframeRollingSchema = z.object({
  items: z.array(campaignTimeslotItemSchema),
  control_label: z.string(),
})

const dimsViewRowSchema = z.object({
  kpi_type: z.string(),
  value_type: z.string().nullable(),
  dims: z.record(z.string()).nullable(),
})

const kpiValuesSchema = z.object({
  items: z.array(z.object({ value: z.string(), label: z.string() })),
  control_label: z.string(),
})

const minMaxDatesSchema = z.object({
  min_date: z.string().nullable(),
  max_date: z.string().nullable(),
  model_period_min: z.string().nullable(),
})

export type KpiMetadataItem = z.infer<typeof kpiMetadataItemSchema>
export type DimsViewRow = z.infer<typeof dimsViewRowSchema>
export type KpiValuesItem = z.infer<typeof kpiValuesSchema>['items'][number]

const controlsRowSchema = z
  .object({
    kpi_metadata: kpiMetadataSchema,
    campaign_timeslots: campaignTimeslotsSchema,
    timeframe_frames: timeframeFramesSchema,
    timeframe_rolling: timeframeRollingSchema,
    dims_view: z.array(dimsViewRowSchema),
    kpi_values: kpiValuesSchema,
    min_max_dates: minMaxDatesSchema,
  })
  .passthrough() // kpi_types is FE-unused; backend may evolve other keys

// PostgREST returns the row wrapped in a single-element array; unwrap to the row.
export const controlsSchema = z
  .array(controlsRowSchema)
  .transform((rows) => rows[0])
  .pipe(controlsRowSchema)

export type Controls = z.infer<typeof controlsSchema>

export async function getControls(client: ApiClient, factTable: string): Promise<Controls> {
  const raw = await client.rpc<unknown>('get_controls', { p_fact_table: factTable })
  return controlsSchema.parse(raw)
}
